import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { hubApi } from '../../lib/hubApi';
import { useHubStore } from '../../store/useHubStore';
import { HubPageHeader } from '../../components/hub/HubPageHeader';

const REQUEST_ID_PREVIEW_LEN = 8;

export function HubGetHelpPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { findings, loadFindings } = useHubStore();

  const findingId = params.get('findingId');
  const finding = useMemo(
    () => findings.find((f) => f.id === findingId),
    [findingId, findings],
  );

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (findings.length === 0) void loadFindings();
  }, [findings.length, loadFindings]);

  // One-shot prefill: when the finding first becomes available, seed the
  // subject/body. Without the ref guard, a refetch that replaces findings
  // (and therefore the finding object reference) would clobber any edits
  // the user has already made.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (finding && !prefilledRef.current) {
      setSubject(`Help with: ${finding.title}`);
      setBody(`Context:\n${finding.context ?? ''}\n\nWhat I'd like TYRL to help with:\n`);
      prefilledRef.current = true;
    }
  }, [finding]);

  const onSubmit = async () => {
    if (submitting) return;
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('subject', subject);
      form.append('body', body);
      if (findingId) form.append('findingId', findingId);
      for (const f of attachments) form.append('attachments', f, f.name);
      const { data } = await hubApi.post<{ id: string }>('/bpx-request', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (findingId) {
        // Server stamps the finding 'bpx_in_progress' inside /bpx-request.
        // Refresh local findings so the row reflects the new status when
        // the user returns to /hub/findings.
        await loadFindings();
      }
      toast.success(`Request ${data.id.slice(0, REQUEST_ID_PREVIEW_LEN)}… sent to TYRL`);
      navigate('/hub/findings');
    } catch (err) {
      const m = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 rounded-md border border-border bg-surface-1 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:border-border-strong';
  const labelClass = 'text-[11px] font-semibold uppercase tracking-[0.14em] text-text-subtle';

  return (
    <section className="space-y-6 max-w-3xl">
      <HubPageHeader
        title="Request help from TYRL"
        subtitle="Your TYRL team handles changes to your org. We triage and respond within your support SLA."
      />

      {finding && (
        <div className="rounded-lg border border-grow/40 bg-grow/10 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-grow">
            Finding attached
          </div>
          <div className="font-medium text-sm text-text-strong mt-1">{finding.title}</div>
          <div className="text-xs text-text-muted mt-1">
            {finding.category} · finding ID {finding.id}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border-subtle bg-surface-2 shadow-card p-5 space-y-4">
        <label className="block space-y-1.5">
          <span className={labelClass}>Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputClass} />
        </label>

        <label className="block space-y-1.5">
          <span className={labelClass}>What do you need?</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className={inputClass}
          />
        </label>

        <div className="space-y-1.5">
          <div className={labelClass}>Attachments</div>
          <input
            type="file"
            multiple
            onChange={(e) => setAttachments(Array.from(e.target.files ?? []))}
            className="text-xs text-text-muted file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-border file:bg-surface-1 file:text-text file:text-xs file:cursor-pointer hover:file:bg-surface-hover"
          />
          {attachments.length > 0 && (
            <ul className="mt-1.5 text-xs text-text-muted">
              {attachments.map((f) => (
                <li key={f.name}>· {f.name} ({Math.round(f.size / 1024)} KB)</li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-text-subtle">
            Audit context (org name, finding IDs, current scores) is auto-attached.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-3.5 py-2 rounded-md border border-border bg-surface-1 text-text text-sm hover:bg-surface-hover hover:border-border-strong transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="px-4 py-2 rounded-md bg-sfblue hover:bg-sfblue/85 disabled:opacity-50 text-sm font-medium text-white transition-colors"
        >
          {submitting ? 'Sending…' : 'Send to TYRL'}
        </button>
      </div>
    </section>
  );
}
